import type {
    Config,
    ContextResponse,
    SummaryResponse,
    ApiErrorResponse,
    SummaryFormat,
    AnalyticsConfigResponse,
    AnalyticsResponse,
    RelativeTimePeriod,
} from '../types';

/**
 * Summaries response from API (multiple summaries)
 */
export interface SummariesResponse {
    summaries: SummaryResponse[];
}

/**
 * Custom error class for API errors
 */
export class WeavableApiError extends Error {
    constructor(
        message: string,
        public statusCode: number,
        public details?: Array<{ message: unknown }>,
    ) {
        super(message);
        this.name = 'WeavableApiError';
    }
}

/**
 * Fetch wrapper with error handling
 */
async function fetchWithAuth(url: string, token: string): Promise<Response> {
    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
            // Bypasses ngrok free-tier browser interstitial; harmless for non-ngrok servers
            // 'ngrok-skip-browser-warning': 'true',
        },
    });

    if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        let details: Array<{ message: unknown }> | undefined;

        try {
            const errorData: ApiErrorResponse = await response.json();
            errorMessage = errorData.error || errorMessage;
            details = errorData.details;
        } catch {
            // If JSON parsing fails, use default error message
        }

        throw new WeavableApiError(errorMessage, response.status, details);
    }

    return response;
}

/**
 * Fetch config for a connection
 * GET /rest/config?connectionId=X
 */
export async function fetchConfig(baseUrl: string, connectionId: string, token: string): Promise<Config> {
    const url = new URL(`${baseUrl}/config`);
    url.searchParams.set('connectionId', connectionId);

    const response = await fetchWithAuth(url.toString(), token);
    return response.json();
}

/**
 * Fetch or create a context
 * GET /rest/context?connectionId=X&fromDateIso=Y&timeZone=Z
 */
export async function fetchContext(
    baseUrl: string,
    connectionId: string,
    fromDateIso: string,
    timeZone: string,
    token: string,
): Promise<ContextResponse> {
    const url = new URL(`${baseUrl}/context`);
    url.searchParams.set('connectionId', connectionId);
    url.searchParams.set('fromDateIso', fromDateIso);
    url.searchParams.set('timeZone', timeZone);

    const response = await fetchWithAuth(url.toString(), token);
    return response.json();
}

/**
 * Fetch multiple summaries for a context
 * GET /rest/summaries?contextId=X&presets=Y,Z&format=W
 */
export async function fetchSummaries(
    baseUrl: string,
    contextId: string,
    presets: string[],
    format: SummaryFormat,
    token: string,
): Promise<SummariesResponse> {
    const url = new URL(`${baseUrl}/summaries`);
    url.searchParams.set('contextId', contextId);
    url.searchParams.set('presets', presets.join(','));
    url.searchParams.set('format', format);

    const response = await fetchWithAuth(url.toString(), token);
    return response.json();
}

/**
 * Fetch analytics configurations for a connection
 * GET /rest/analytics/config?connectionId=X
 */
export async function fetchAnalyticsConfig(
    baseUrl: string,
    connectionId: string,
    token: string,
    preset?: string,
): Promise<AnalyticsConfigResponse> {
    const url = new URL(`${baseUrl}/analytics/config`);
    url.searchParams.set('connectionId', connectionId);
    if (preset) {
        url.searchParams.set('preset', preset);
    }

    const response = await fetchWithAuth(url.toString(), token);
    return response.json();
}

/**
 * Execute analytics and get visualization result
 * GET /rest/analytics?connectionId=X&analyticsId=Y&timePeriod=Z&userIanaZone=W&includeDelta=V&param.<key>=<value>
 *
 * `parameters` is a flat map of insight-specific parameter overrides for the single insight
 * being fetched (e.g. `{ lookAheadDays: 30 }`). Each entry is sent on the wire as
 * `param.<key>=<value>` and coerced/validated server-side against the insight's declared schema.
 */
export async function fetchAnalytics(
    baseUrl: string,
    connectionId: string,
    analyticsId: string,
    timePeriod: RelativeTimePeriod,
    userIanaZone: string | undefined,
    includeDelta: boolean | undefined,
    token: string,
    parameters?: Record<string, number | boolean | string>,
): Promise<AnalyticsResponse> {
    const url = new URL(`${baseUrl}/analytics`);
    url.searchParams.set('connectionId', connectionId);
    url.searchParams.set('analyticsId', analyticsId);
    url.searchParams.set('timePeriod', timePeriod);
    if (userIanaZone) {
        url.searchParams.set('userIanaZone', userIanaZone);
    }
    if (includeDelta !== undefined) {
        url.searchParams.set('includeDelta', includeDelta ? 'true' : 'false');
    }
    if (parameters) {
        for (const [key, value] of Object.entries(parameters)) {
            url.searchParams.set(`param.${key}`, String(value));
        }
    }

    const response = await fetchWithAuth(url.toString(), token);
    return response.json();
}
